/**
 * @file CarrierDeliveryFields.tsx
 * @description Поля расчёта доставки в настройках перевозчика (phase19.12).
 *
 * Вынесено из ShopCarrierConfigDialog: там уже 300+ строк, а тут добавляется
 * ещё десяток полей (правило ≤200 строк на компонент).
 *
 * Блоки: точка отправления, посылка по умолчанию, тарифы на витрине,
 * цена для клиента, ключ карты для виджета выбора ПВЗ.
 */

import { SenderSection } from "./SenderSection";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  SDEK_TARIFF_OPTIONS,
  type CarrierHandoverMode,
  type CarrierKind,
} from "../../lib/api-shop-carriers";

/** Строковая форма — числа парсим при сохранении (пустая строка = null). */
export interface DeliveryFormState {
  senderCityCode: string;
  senderPostalCode: string;
  senderAddress: string;
  senderPickupPointCode: string;
  handoverMode: CarrierHandoverMode;
  defaultBoxLengthMm: string;
  defaultBoxWidthMm: string;
  defaultBoxHeightMm: string;
  fallbackWeightG: string;
  publicTariffCodes: number[];
  markupPercent: string;
  markupAmount: string;
  freeShippingThreshold: string;
  yandexMapsApiKey: string;
}

interface Props {
  value: DeliveryFormState;
  onChange: (patch: Partial<DeliveryFormState>) => void;
  /** Нужен блоку отправления: поиск ПВЗ и справочник городов перевозчика. */
  carrier: CarrierKind;
  disabled?: boolean;
}

const SECTION = "text-xs font-semibold uppercase tracking-wide text-on-surface-variant";

export function CarrierDeliveryFields({ value, onChange, carrier, disabled }: Props) {
  const toggleTariff = (code: number): void => {
    const next = value.publicTariffCodes.includes(code)
      ? value.publicTariffCodes.filter((c) => c !== code)
      : [...value.publicTariffCodes, code].sort((a, b) => a - b);
    onChange({ publicTariffCodes: next });
  };

  return (
    <div className="flex flex-col gap-6">
      {/* ── Отправление (статус + пункт сдачи + адрес склада) ── */}
      <SenderSection
        carrier={carrier}
        value={value}
        onChange={onChange}
        mapApiKey={value.yandexMapsApiKey}
        disabled={disabled}
      />

      {/* ── Посылка по умолчанию ── */}
      <section className="flex flex-col gap-3">
        <div className={SECTION}>Посылка по умолчанию</div>
        <div className="grid grid-cols-3 gap-2">
          {(["defaultBoxLengthMm", "defaultBoxWidthMm", "defaultBoxHeightMm"] as const).map(
            (field, idx) => (
              <div key={field} className="flex flex-col gap-1.5">
                <Label htmlFor={field}>{["Длина, мм", "Ширина, мм", "Высота, мм"][idx]}</Label>
                <Input
                  id={field}
                  inputMode="numeric"
                  value={value[field]}
                  disabled={disabled}
                  onChange={(e) => onChange({ [field]: e.target.value })}
                />
              </div>
            ),
          )}
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="fallbackWeightG">Запасной вес позиции, г</Label>
          <Input
            id="fallbackWeightG"
            inputMode="numeric"
            placeholder="400"
            value={value.fallbackWeightG}
            disabled={disabled}
            onChange={(e) => onChange({ fallbackWeightG: e.target.value })}
          />
          <p className="text-xs text-on-surface-variant">
            Используется, если у товара не заполнен вес. Менеджер получит предупреждение.
          </p>
        </div>
      </section>

      {/* ── Тарифы на витрине ── */}
      <section className="flex flex-col gap-2">
        <div className={SECTION}>Тарифы на витрине</div>
        {SDEK_TARIFF_OPTIONS.map((t) => (
          <label key={t.code} className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={value.publicTariffCodes.includes(t.code)}
              disabled={disabled}
              onChange={() => toggleTariff(t.code)}
            />
            <span className="tabular-nums text-on-surface-variant">{t.code}</span>
            {t.label}
          </label>
        ))}
        <p className="text-xs text-on-surface-variant">
          Ничего не выбрано — клиенту покажем все доступные тарифы.
        </p>
      </section>

      {/* ── Цена для клиента ── */}
      <section className="flex flex-col gap-3">
        <div className={SECTION}>Цена для клиента</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="markupPercent">Наценка, %</Label>
            <Input
              id="markupPercent"
              inputMode="decimal"
              placeholder="0"
              value={value.markupPercent}
              disabled={disabled}
              onChange={(e) => onChange({ markupPercent: e.target.value })}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="markupAmount">Надбавка, ₽</Label>
            <Input
              id="markupAmount"
              inputMode="decimal"
              placeholder="0"
              value={value.markupAmount}
              disabled={disabled}
              onChange={(e) => onChange({ markupAmount: e.target.value })}
            />
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="freeShippingThreshold">Бесплатная доставка от, ₽</Label>
          <Input
            id="freeShippingThreshold"
            inputMode="decimal"
            placeholder="не включена"
            value={value.freeShippingThreshold}
            disabled={disabled}
            onChange={(e) => onChange({ freeShippingThreshold: e.target.value })}
          />
          <p className="text-xs text-on-surface-variant">
            Считается по сумме товаров, без стоимости доставки.
          </p>
        </div>
      </section>

      {/* ── Карта ПВЗ (ключ переехал в Интеграции, phase19.12) ── */}
      <section className="flex flex-col gap-1.5">
        <div className={SECTION}>Карта пунктов выдачи</div>
        <p className="text-xs text-on-surface-variant">
          {value.yandexMapsApiKey
            ? "Ключ Яндекс.Карт подключён — карта доступна и в админке, и на витрине."
            : "Без рабочего ключа Яндекса витрина покажет карту OpenStreetMap (режимы «Авто» и «OpenStreetMap»); в режиме «Яндекс.Карты» без ключа карта не откроется — останется выбор списком."}
          {" Ключ и секрет задаются в разделе «Интеграции» → «Яндекс.Карты»."}
        </p>
      </section>
    </div>
  );
}
